import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { AipBillingTransferForm } from './aip-billing-transfer-form';
import utc from 'dayjs/plugin/utc';

// Self-contained: this component renders OP epoch dates in UTC, so it must
// register the plugin itself rather than rely on a sibling module's import.
// extend() is idempotent.
dayjs.extend(utc);

type Result = Awaited<ReturnType<typeof rpx.accounts.getMyAipBilling>>;
type Snapshot = Extract<Result, { kind: 'snapshot' }>;
type Transfer = Awaited<ReturnType<typeof rpx.aipBillingTransfer.getMyAipBillingTransfer>>;

const SUPPORT_HREF = 'mailto:aip@my.mirasee.com?subject=AI%20Playground%20membership';

export function AipBillingCard() {
  const [result, setResult] = useState<Result | undefined>(undefined);
  const [transfer, setTransfer] = useState<Transfer | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    rpx.accounts
      .getMyAipBilling()
      .then((r) => {
        if (!cancelled) {
          setResult(r);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });
    // A failed transfer lookup leaves the card in its normal read-only state
    // rather than blanking it — the snapshot below is the more important half.
    rpx.aipBillingTransfer
      .getMyAipBillingTransfer()
      .then((t) => {
        if (!cancelled) {
          setTransfer(t);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Render nothing until we know what to show — avoids a phantom skeleton for
  // app-paying members (a large share of AIP) who resolve to 'hidden'.
  if (!result && !failed) {
    return undefined;
  }
  if (result?.kind === 'hidden') {
    return undefined;
  }

  const pendingTransfer = transfer?.kind === 'pending' ? transfer : undefined;
  const doneTransfer = transfer?.kind === 'done' ? transfer : undefined;
  // The first-charge date ops picked is under 3 days away (or past), so the server stopped serving
  // the card form. Say so: dropping silently back to the read-only "email us"
  // body reads as the card they entered vanishing, and leaves them with no idea
  // that someone has to re-issue the transfer.
  const isExpired = transfer?.kind === 'expired';
  // The Ontraport charge date is about to stop being the one that matters, and
  // two different "next charge" dates on one card is worse than none.
  const movingBilling = !!pendingTransfer || !!doneTransfer;

  return (
    <div class="rounded-md border border-indigo-100 bg-indigo-50/60 p-4 flex flex-col gap-3">
      <strong class="text-gray-900">Your AI Playground membership</strong>
      {result?.kind === 'snapshot' && result.status !== 'unknown' && (
        <SnapshotStatusLine data={result} hideNextCharge={movingBilling} />
      )}
      {pendingTransfer ? (
        <AipBillingTransferForm
          transfer={pendingTransfer}
          onDone={() => setTransfer({ kind: 'done', firstChargeOn: pendingTransfer.firstChargeOn })}
        />
      ) : doneTransfer ? (
        <TransferDoneBody firstChargeOn={doneTransfer.firstChargeOn} />
      ) : isExpired ? (
        <TransferExpiredBody />
      ) : result?.kind === 'snapshot' && result.status !== 'unknown' ? (
        <SnapshotHelpText data={result} />
      ) : (
        <FallbackBody />
      )}
    </div>
  );
}

function TransferDoneBody({ firstChargeOn }: { firstChargeOn: string }) {
  return (
    <p class="text-sm text-gray-700">
      Your membership is now billed here. First charge{' '}
      {dayjs.utc(`${firstChargeOn}T00:00:00Z`).format('MMMM D, YYYY')}. We will cancel your
      Ontraport billing.
    </p>
  );
}

function TransferExpiredBody() {
  return (
    <p class="text-sm text-gray-700">
      The date we set for moving your AI Playground membership to this site is too close, so the
      card form is closed. Nothing has changed about your access or your current billing. Email{' '}
      <a href={SUPPORT_HREF} class="text-indigo-600 hover:underline">
        aip@my.mirasee.com
      </a>{' '}
      and we will set a new date for you.
    </p>
  );
}

function SnapshotStatusLine({ data, hideNextCharge }: { data: Snapshot; hideNextCharge: boolean }) {
  if (data.status === 'cancelled') {
    return <StatusPill status="cancelled" />;
  }

  // Only show a price when we know the cadence — a bare "$97" with no
  // /month or /year is ambiguous.
  const amount = data.term ? formatAmount(data.amountCents, data.term) : undefined;
  const isTrial = data.status === 'free_trial';
  const amountLine = amount && isTrial ? `${amount} after your trial` : amount;

  return (
    <div class="flex flex-wrap items-baseline gap-2 text-sm">
      <StatusPill status={data.status} />
      {amountLine && <span class="font-medium text-gray-900">{amountLine}</span>}
      {data.nextChargeAt && !hideNextCharge && (
        <span class="text-gray-600">
          · Next charge {dayjs.utc(data.nextChargeAt).format('MMM D, YYYY')}
        </span>
      )}
    </div>
  );
}

function SnapshotHelpText({ data }: { data: Snapshot }) {
  // A cancelled membership keeps a populated amount/next-order in Ontraport
  // until automation clears it, so don't show a price or charge date.
  if (data.status === 'cancelled') {
    return (
      <p class="text-sm text-gray-600">
        Your AI Playground membership is cancelled. Questions? Email{' '}
        <a href={SUPPORT_HREF} class="text-indigo-600 hover:underline">
          aip@my.mirasee.com
        </a>
        .
      </p>
    );
  }

  return (
    <p class="text-sm text-gray-600">
      Your membership is billed separately from any individual courses listed here.{' '}
      {data.nextChargeAt
        ? 'To pause or cancel it, email '
        : 'To confirm your exact renewal date, pause, or cancel it, email '}
      <a href={SUPPORT_HREF} class="text-indigo-600 hover:underline">
        aip@my.mirasee.com
      </a>
      .
    </p>
  );
}

function FallbackBody() {
  return (
    <p class="text-sm text-gray-600">
      Your AI Playground membership is billed and managed separately from any individual courses
      listed here — any AI Playground courses you have are included at no extra charge. To check
      your renewal, pause, or cancel your membership, email{' '}
      <a href={SUPPORT_HREF} class="text-indigo-600 hover:underline">
        aip@my.mirasee.com
      </a>{' '}
      and our team will take care of it.
    </p>
  );
}

function StatusPill({ status }: { status: Snapshot['status'] }) {
  const { label, classes } = statusStyle(status);
  return <span class={`rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}>{label}</span>;
}

function statusStyle(status: Snapshot['status']): { label: string; classes: string } {
  switch (status) {
    case 'active':
      return { label: 'Active', classes: 'bg-green-50 text-green-700' };
    case 'free_trial':
      return { label: 'Free trial', classes: 'bg-blue-50 text-blue-700' };
    case 'cancelled':
      return { label: 'Cancelled', classes: 'bg-gray-100 text-gray-700' };
    case 'pending':
      return { label: 'Pending', classes: 'bg-amber-50 text-amber-800' };
    default:
      return { label: 'Member', classes: 'bg-gray-100 text-gray-600' };
  }
}

function formatAmount(
  cents: number | undefined,
  term: 'monthly' | 'yearly' | undefined,
): string | undefined {
  if (!cents) {
    return;
  }
  const dollars = cents / 100;
  const amount = `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
  if (term === 'monthly') {
    return `${amount}/month`;
  }
  if (term === 'yearly') {
    return `${amount}/year`;
  }
  return amount;
}
